import Button from '@/presentation/components/elements/Button';
import FormattedEther from '@/presentation/components/elements/FormattedEther';
import Icon, { Icons } from '@/presentation/components/elements/Icon';
import Price from '@/presentation/components/elements/Price';
import VaultIcon from '@/presentation/components/elements/VaultIcon';
import { InventoryPosition, LiquidityPosition, Vault } from 'nftx.js';
import { Dispatch, SetStateAction } from 'react';

const toggleItem = <T extends { id: string }>(item: T, arr: T[]) => {
  const array = arr.slice(0);
  // Find the index of the item with the same 'id' as the itemToToggle
  const index = array.findIndex((e) => e.id === item.id);

  // If the item is found, remove it
  if (index !== -1) {
    array.splice(index, 1);
  } else {
    // If the item is not found, add it
    array.push(item);
  }
  return array;
};

const ClaimRewardsReady = ({
  liquidityPositions,
  inventoryPositions,
  vaults,
  onClaim,
  lpClaims,
  setLpClaims,
  ipClaims,
  setIpClaims,
}: {
  liquidityPositions: Array<LiquidityPosition>;
  inventoryPositions: Array<InventoryPosition>;
  vaults: Vault[];
  onClaim: () => void;
  lpClaims: LiquidityPosition[];
  setLpClaims: Dispatch<SetStateAction<LiquidityPosition[]>>;
  ipClaims: InventoryPosition[];
  setIpClaims: Dispatch<SetStateAction<InventoryPosition[]>>;
}) => {
  return (
    <div className="space-y-4">
      <h1 className="font-medium text-lg">Claim rewards</h1>
      {liquidityPositions.length > 0 && (
        <div>
          <header className="flex justify-between items-center">
            <h2 className="font-medium text-gray-500 mb-2">
              Liquidity rewards
            </h2>
            <span className="text-xs text-gray-800 font-medium">
              {lpClaims.length}
              {' selected'}
            </span>
          </header>
          <dl className="space-y-2">
            {liquidityPositions.map((position) => {
              const { id, vaultId, claimableEth, claimableVToken } = position;
              const selected = lpClaims.some((x) => x.id === id);
              return (
                <button
                  className={`w-full px-4 py-2 rounded-lg border bg-gray-50 text-sm ${
                    selected ? 'border-pink-500' : 'border-gray-300'
                  }`}
                  key={id}
                  type="button"
                  onClick={() =>
                    setLpClaims((arr) => toggleItem(position, arr))
                  }
                >
                  <div className="flex justify-between">
                    <dt className="flex items-center font-bold">
                      <VaultIcon vaultId={vaultId} className="mr-2 w-5" />
                      {vaults?.find((v) => v.vaultId === vaultId)?.token
                        .symbol ?? ''}
                    </dt>
                    <dd className="font-normal text-base pl-4">
                      <FormattedEther
                        value={claimableVToken}
                        maximumSignificantDigits={2}
                      />
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="flex items-center font-bold">
                      <Icon name={Icons.ETH} className="mr-2" size={20} />
                      <span>{'ETH'}</span>
                    </dt>
                    <dd className="font-normal text-base pl-4">
                      <FormattedEther
                        value={claimableEth}
                        maximumSignificantDigits={2}
                      />
                    </dd>
                  </div>
                </button>
              );
            })}
          </dl>
        </div>
      )}

      {inventoryPositions.length > 0 && (
        <div>
          <header className="flex justify-between items-center">
            <h2 className="font-medium text-gray-500 mb-2">
              Inventory rewards
            </h2>
            <span className="text-xs text-gray-800 font-medium">
              {ipClaims.length}
              {' selected'}
            </span>
          </header>
          <dl className="space-y-2">
            {inventoryPositions.map((position) => {
              const { id, vaultId, claimableRewards } = position;
              const selected = ipClaims.some((x) => x.id === id);
              return (
                <button
                  className={`w-full px-4 py-2 rounded-lg border bg-gray-50 text-sm ${
                    selected ? 'border-pink-500' : 'border-gray-300'
                  }`}
                  key={id}
                  type="button"
                  onClick={() =>
                    setIpClaims((arr) => toggleItem(position, arr))
                  }
                >
                  <div className="flex justify-between">
                    <dt className="flex items-center font-bold">
                      <VaultIcon vaultId={vaultId} className="mr-2 w-5" />
                      {vaults?.find((v) => v.vaultId === vaultId)?.token
                        .symbol ?? ''}
                    </dt>
                    <dd className="font-normal text-base pl-4">
                      <Price
                        value={claimableRewards}
                        currency="ETH"
                        maximumSignificantDigits={2}
                      />
                    </dd>
                  </div>
                </button>
              );
            })}
          </dl>
        </div>
      )}

      {!!(ipClaims.length || lpClaims.length) && (
        <div>
          <Button type="button" onClick={onClaim} className="w-full">
            {'Claim'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ClaimRewardsReady;
